import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  FileText, Plus, Save, History, RotateCcw, Trash2, Search,
  Bold, Italic, Underline, List, ListOrdered, Heading1, Heading2,
  Undo2, Redo2, X, Edit3, Clock, Archive, CheckCircle2, Printer
} from 'lucide-react';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';
import { BOOK_CSS, extractTOC, numberChapters, buildBookPrintHtml, substituteClinic } from '../utils/bookRender';
import { useClinic } from '../context/ClinicContext';

// ═══════════════════════════════════════════════════════════════════
//  EditableDocsPage — generic editable-document CRUD page with
//  revision history + print. Reused for Manuals / Policies /
//  Organogram via the `type` prop.
// ═══════════════════════════════════════════════════════════════════

const fmtDate = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const EditableDocsPage = ({ API_BASE, currentUser, type, label, icon, description }) => {
  const { clinic } = useClinic();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyDoc, setHistoryDoc] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [draftTitle, setDraftTitle] = useState('');
  const [changeNotes, setChangeNotes] = useState('');
  const editorRef = useRef(null);

  const isAdmin = ['owner','admin','super_admin'].includes(currentUser?.role);
  const showMsg = (t, text) => { setMsg({ type: t, text }); setTimeout(() => setMsg({ type: '', text: '' }), 3500); };

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/documents`, { params: { type } });
      setDocs(res.data || []);
    } catch (e) { showMsg('error', 'Load failed'); }
    finally { setLoading(false); }
  }, [API_BASE, type]);

  const loadOne = useCallback(async (id) => {
    try {
      const res = await axios.get(`${API_BASE}/documents/${id}`);
      setSelectedDoc(res.data);
      setDraftTitle(res.data.title);
      setEditMode(false);
      if (editorRef.current) editorRef.current.innerHTML = res.data.content || '';
    } catch (e) { showMsg('error', 'Doc load failed'); }
  }, [API_BASE]);

  // Reset view when type changes (switching Manuals → Policies etc.)
  useEffect(() => {
    setSelectedId(null);
    setSelectedDoc(null);
    setEditMode(false);
    setSearch('');
  }, [type]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (selectedId) loadOne(selectedId); }, [selectedId, loadOne]);

  const filtered = useMemo(() => {
    if (!search.trim()) return docs;
    const q = search.toLowerCase();
    return docs.filter(d =>
      (d.title || '').toLowerCase().includes(q) ||
      (d.code || '').toLowerCase().includes(q) ||
      (d.summary || '').toLowerCase().includes(q)
    );
  }, [docs, search]);

  const exec = (cmd, value = null) => { document.execCommand(cmd, false, value); editorRef.current?.focus(); };

  const startEdit = () => {
    setEditMode(true); setChangeNotes('');
    setTimeout(() => { if (editorRef.current) editorRef.current.innerHTML = selectedDoc.content || ''; editorRef.current?.focus(); }, 0);
  };
  const cancelEdit = () => {
    setEditMode(false); setChangeNotes(''); setDraftTitle(selectedDoc.title);
    if (editorRef.current) editorRef.current.innerHTML = selectedDoc.content || '';
  };
  const saveEdit = async () => {
    if (!selectedDoc) return;
    setSaving(true);
    try {
      const content = editorRef.current?.innerHTML || '';
      const res = await axios.put(`${API_BASE}/documents/${selectedDoc._id}`, {
        title: draftTitle, content, changeNotes, editedBy: currentUser?.fullName || currentUser?.username || 'admin',
      });
      setSelectedDoc(res.data); setEditMode(false); setChangeNotes('');
      showMsg('success', `Saved as v${res.data.version}`); loadList();
    } catch (e) { showMsg('error', 'Save failed: ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };
  const loadHistory = async () => {
    try { const res = await axios.get(`${API_BASE}/documents/${selectedDoc._id}/history`); setHistoryDoc(res.data); setShowHistory(true); }
    catch { showMsg('error', 'History load failed'); }
  };
  const revertTo = async (v) => {
    if (!window.confirm(`Version ${v} pe revert karein?`)) return;
    try {
      const res = await axios.post(`${API_BASE}/documents/${selectedDoc._id}/revert/${v}`, { editedBy: currentUser?.fullName || 'admin' });
      setSelectedDoc(res.data);
      if (editorRef.current) editorRef.current.innerHTML = res.data.content || '';
      setShowHistory(false); showMsg('success', `Reverted to v${v}`); loadList();
    } catch { showMsg('error', 'Revert failed'); }
  };
  const deleteDoc = async () => {
    if (!selectedDoc) return;
    if (!window.confirm(`"${selectedDoc.title}" archive kar dein?`)) return;
    try { await axios.delete(`${API_BASE}/documents/${selectedDoc._id}`); showMsg('success', 'Archived'); setSelectedId(null); setSelectedDoc(null); loadList(); }
    catch { showMsg('error', 'Delete failed'); }
  };
  const createDoc = async (payload) => {
    try {
      const res = await axios.post(`${API_BASE}/documents`, {
        ...payload, type,
        createdBy: currentUser?.fullName || currentUser?.username || 'admin',
      });
      setShowCreate(false); loadList(); setSelectedId(res.data._id); showMsg('success', 'Created');
    } catch (e) { showMsg('error', e.response?.data?.error || e.message); }
  };

  const printDoc = () => {
    if (!selectedDoc) return;
    const w = window.open('', '_blank', 'width=900,height=700');
    w.document.write(buildBookPrintHtml(selectedDoc, clinic));
    w.document.close();
  };

  return (
    <div>
      {/* ─── HEADER ─── */}
      <div style={{ ...cardStyle, marginBottom: 16, background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`, color: 'white', border: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, color: 'white', fontSize: '1.4rem' }}>{icon} {label}</h2>
            <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: 13 }}>
              {docs.length} documents · {description || 'editable with revision history · print-ready'}
            </p>
          </div>
          {isAdmin && (
            <button onClick={() => setShowCreate(true)} style={{ ...btnStyle, marginTop: 0, backgroundColor: 'rgba(255,255,255,0.18)', color: 'white', border: '1px solid rgba(255,255,255,0.4)' }}>
              <Plus size={16} style={{ marginRight: 6 }} /> New {label.replace(/s$/, '')}
            </button>
          )}
        </div>
      </div>

      {msg.text && (
        <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 8,
          background: msg.type === 'error' ? '#fee' : '#efe',
          color: msg.type === 'error' ? theme.danger : theme.success,
          border: `1px solid ${msg.type === 'error' ? theme.danger : theme.success}44`,
          fontWeight: 600, fontSize: 13.5,
        }}>{msg.text}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: selectedDoc ? '320px 1fr' : '1fr', gap: 12 }}>
        {/* LIST */}
        <div style={{ ...cardStyle, padding: 0, maxHeight: 'calc(100vh - 100px)', overflowY: 'auto' }}>
          <div style={{ padding: 10, borderBottom: `1px solid ${theme.borderLight}`, position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
            <div style={{ position: 'relative' }}>
              <Search size={14} color={theme.textMuted} style={{ position: 'absolute', left: 8, top: 9 }} />
              <input placeholder={`Search ${label.toLowerCase()}…`} value={search} onChange={e => setSearch(e.target.value)}
                style={{ ...inputStyle, padding: '6px 8px 6px 28px', fontSize: 12.5, width: '100%', boxSizing: 'border-box' }} />
            </div>
          </div>
          {loading ? (
            <div style={{ padding: 30, textAlign: 'center', color: theme.textMuted }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted }}>
              <FileText size={36} color={theme.textSubtle} style={{ marginBottom: 8, opacity: 0.5 }} />
              <div style={{ fontWeight: 600 }}>Koi document nahi</div>
              <div style={{ fontSize: 12 }}>Upar "New" click karke banao</div>
            </div>
          ) : filtered.map(d => (
            <div key={d._id} onClick={() => setSelectedId(d._id)} style={{
              padding: '10px 12px', borderBottom: `1px solid ${theme.borderLight}`, cursor: 'pointer',
              background: selectedId === d._id ? theme.primary + '14' : 'transparent',
              borderLeft: selectedId === d._id ? `3px solid ${theme.primary}` : '3px solid transparent',
            }}>
              {d.code && <div style={{ fontSize: 10, fontWeight: 700, color: theme.primaryDark, background: theme.primary + '18', padding: '1px 6px', borderRadius: 4, display: 'inline-block', marginBottom: 2 }}>{d.code}</div>}
              <div style={{ fontSize: 13, fontWeight: 600, color: theme.navy, lineHeight: 1.3 }}>{substituteClinic(d.title, clinic)}</div>
              <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>v{d.version} · {fmtDate(d.lastEditedAt || d.updatedAt)}</div>
            </div>
          ))}
        </div>

        {/* EDITOR */}
        {selectedDoc && (
          <div style={{ ...cardStyle, padding: 0, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 100px)' }}>
            <div style={{ padding: '14px 18px', borderBottom: `1px solid ${theme.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                {selectedDoc.code && <span style={{ fontSize: 11, fontWeight: 700, color: theme.primaryDark, background: theme.primary + '18', padding: '3px 8px', borderRadius: 5 }}>{selectedDoc.code}</span>}
                <span style={{ fontSize: 11, color: theme.textMuted }}>v{selectedDoc.version} · {selectedDoc.revisions.length} revisions</span>
                {selectedDoc.lastEditedAt && <span style={{ fontSize: 11, color: theme.textMuted }}>· Last: {fmtDate(selectedDoc.lastEditedAt)} by {selectedDoc.lastEditedBy || '—'}</span>}
              </div>
              {editMode ? (
                <input value={draftTitle} onChange={e => setDraftTitle(e.target.value)}
                  style={{ ...inputStyle, fontSize: 18, fontWeight: 700, padding: '6px 8px', width: '100%', boxSizing: 'border-box' }} />
              ) : (
                <h3 style={{ margin: 0, color: theme.navy, fontSize: 17 }}>{selectedDoc.title}</h3>
              )}
              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                {!editMode ? (
                  <>
                    {isAdmin && <button onClick={startEdit} style={toolBtn(true)}><Edit3 size={13} /> Edit</button>}
                    <button onClick={loadHistory} style={toolBtn()}><History size={13} /> History</button>
                    <button onClick={printDoc} style={toolBtn()}><Printer size={13} /> Print</button>
                    {isAdmin && <button onClick={deleteDoc} style={{ ...toolBtn(), color: theme.danger, borderColor: theme.danger + '55' }}><Archive size={13} /> Archive</button>}
                  </>
                ) : (
                  <>
                    <button onClick={() => exec('bold')} style={toolBtn()}><Bold size={13} /></button>
                    <button onClick={() => exec('italic')} style={toolBtn()}><Italic size={13} /></button>
                    <button onClick={() => exec('underline')} style={toolBtn()}><Underline size={13} /></button>
                    <span style={{ width: 1, height: 20, background: theme.border }} />
                    <button onClick={() => exec('formatBlock', '<h1>')} style={toolBtn()}><Heading1 size={13} /></button>
                    <button onClick={() => exec('formatBlock', '<h2>')} style={toolBtn()}><Heading2 size={13} /></button>
                    <button onClick={() => exec('formatBlock', '<p>')} style={toolBtn()}>P</button>
                    <span style={{ width: 1, height: 20, background: theme.border }} />
                    <button onClick={() => exec('insertUnorderedList')} style={toolBtn()}><List size={13} /></button>
                    <button onClick={() => exec('insertOrderedList')} style={toolBtn()}><ListOrdered size={13} /></button>
                    <span style={{ width: 1, height: 20, background: theme.border }} />
                    <button onClick={() => exec('undo')} style={toolBtn()}><Undo2 size={13} /></button>
                    <button onClick={() => exec('redo')} style={toolBtn()}><Redo2 size={13} /></button>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                      <input placeholder="Change notes (optional)" value={changeNotes} onChange={e => setChangeNotes(e.target.value)}
                        style={{ ...inputStyle, fontSize: 12, padding: '5px 8px', width: 200 }} />
                      <button onClick={cancelEdit} style={toolBtn()}><X size={13} /> Cancel</button>
                      <button onClick={saveEdit} disabled={saving} style={{ ...toolBtn(true), background: theme.success + '22', color: theme.success, borderColor: theme.success + '55' }}>
                        <Save size={13} /> {saving ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {editMode ? (
                <div ref={editorRef} contentEditable suppressContentEditableWarning
                  style={{ minHeight: 300, outline: 'none', fontSize: 14.5, lineHeight: 1.7, color: theme.navy, padding: '18px 22px' }} />
              ) : (
                <BookReader doc={selectedDoc} clinic={clinic} />
              )}
            </div>
          </div>
        )}
      </div>

      {showHistory && historyDoc && (
        <div style={modalBackdrop} onClick={() => setShowHistory(false)}>
          <div style={{ ...modalStyle, maxWidth: 640 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <h3 style={{ margin: 0, color: theme.navy }}><Clock size={18} style={{ verticalAlign: -3, marginRight: 6 }} />History — {historyDoc.title}</h3>
              <button onClick={() => setShowHistory(false)} style={toolBtn()}><X size={14} /></button>
            </div>
            <div style={{ maxHeight: 440, overflowY: 'auto' }}>
              {historyDoc.revisions.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: theme.textMuted }}>No prior revisions.</div>
              ) : [...historyDoc.revisions].reverse().map(r => (
                <div key={r.version} style={{ padding: '10px 12px', border: `1px solid ${theme.border}`, borderRadius: 8, marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, color: theme.primaryDark }}>Version {r.version}</span>
                    <button onClick={() => revertTo(r.version)} style={{ ...toolBtn(), fontSize: 11 }}><RotateCcw size={11} /> Revert</button>
                  </div>
                  <div style={{ fontSize: 11.5, color: theme.textMuted, marginBottom: 4 }}>{fmtDate(r.editedAt)} · {r.editedBy || '—'}</div>
                  {r.changeNotes && <div style={{ fontSize: 12, color: theme.navy, fontStyle: 'italic', marginBottom: 6 }}>"{r.changeNotes}"</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreate={createDoc} labelSingular={label.replace(/s$/, '')} />}
    </div>
  );
};

// ── Book Reader — cover + numbered index + chapter-numbered body ──
const BookReader = ({ doc, clinic }) => {
  const liveTitle = useMemo(() => substituteClinic(doc.title || '', clinic), [doc.title, clinic]);
  const liveContent = useMemo(() => substituteClinic(doc.content || '', clinic), [doc.content, clinic]);
  const liveCode = useMemo(() => substituteClinic(doc.code || '', clinic), [doc.code, clinic]);
  const numberedHtml = useMemo(() => numberChapters(liveContent), [liveContent]);
  const toc = useMemo(() => extractTOC(numberedHtml), [numberedHtml]);
  const [currentChapter, setCurrentChapter] = useState('');
  const containerRef = useRef(null);

  const jump = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Track currently-visible H1 to show as running header
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const headings = root.querySelectorAll('h1');
    if (headings.length === 0) return;
    const onScroll = () => {
      let active = '';
      headings.forEach(h => {
        const rect = h.getBoundingClientRect();
        const contRect = root.getBoundingClientRect();
        if (rect.top - contRect.top < 80) active = h.textContent.replace(/^Chapter\s+\d+\s*/i, '').trim();
      });
      setCurrentChapter(active);
    };
    onScroll();
    root.addEventListener('scroll', onScroll, { passive: true });
    return () => root.removeEventListener('scroll', onScroll);
  }, [numberedHtml]);

  const firstLetter = liveTitle.trim().charAt(0).toUpperCase() || '?';
  const lastEdit = doc.lastEditedAt ? new Date(doc.lastEditedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) : '';

  return (
    <div ref={containerRef} style={{ height: '100%', overflowY: 'auto', position: 'relative', background: '#fafaf7' }}>
      <style>{BOOK_CSS}</style>

      {/* Sticky running header — current chapter */}
      {currentChapter && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 2,
          background: '#fff', borderBottom: `1px solid ${theme.borderLight}`,
          padding: '6px 18px', textAlign: 'center',
          fontFamily: 'Georgia, serif', fontSize: 11, color: theme.textMuted,
          letterSpacing: 1.2, textTransform: 'uppercase',
        }}>
          {currentChapter.length > 80 ? currentChapter.slice(0, 78) + '…' : currentChapter}
        </div>
      )}

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '10px 16px 50px' }}>
        {/* Cover */}
        <div className="book-cover">
          <div className="drop-letter">{firstLetter}</div>
          <div className="ornament">❦ &nbsp; ✦ &nbsp; ❦</div>
          <div className="book-title">{liveTitle}</div>
          {liveCode && <div className="book-sub">{liveCode}</div>}
          {clinic?.clinicName && <div style={{ fontSize: 12, color: theme.navy, fontWeight: 600, marginTop: 6, letterSpacing: 0.4 }}>{clinic.clinicName}</div>}
          <hr style={{ width: 80, border: 0, borderTop: `1px solid ${theme.border}`, margin: '16px auto' }} />
          <div style={{ fontSize: 12.5, color: theme.textMuted }}>
            Version {doc.version || 1} · {lastEdit}{doc.lastEditedBy ? ' · by ' + doc.lastEditedBy : ''}
          </div>
        </div>

        {/* Index (TOC with dot leaders + section numbers) */}
        {toc.length > 0 && (
          <div className="book-index">
            <div className="idx-title">INDEX</div>
            {toc.map(t => (
              <div key={t.id} className={`idx-item l${t.level}`} onClick={() => jump(t.id)}>
                <span className="idx-name">
                  <span style={{ color: '#64748b', marginRight: 8, fontVariantNumeric: 'tabular-nums' }}>{t.num}</span>
                  {t.text}
                </span>
                <span className="idx-dots" />
                <span className="idx-num">§ {t.num}</span>
              </div>
            ))}
            <div style={{ marginTop: 10, fontSize: 11, color: theme.textMuted, textAlign: 'center', fontStyle: 'italic' }}>
              Click any entry to jump · real page numbers shown in Print view
            </div>
          </div>
        )}

        {/* Numbered content */}
        <div className="book-view" dangerouslySetInnerHTML={{ __html: numberedHtml || '<p style="color:#9ca3af">Empty — click Edit to add content.</p>' }} />
      </div>
    </div>
  );
};

const CreateModal = ({ onClose, onCreate, labelSingular }) => {
  const [form, setForm] = useState({ code: '', title: '', summary: '' });
  const submit = (e) => { e.preventDefault(); if (!form.title.trim()) return; onCreate(form); };
  return (
    <div style={modalBackdrop} onClick={onClose}>
      <form onSubmit={submit} style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 14px', color: theme.navy }}><Plus size={18} style={{ verticalAlign: -3, marginRight: 6 }} />New {labelSingular}</h3>
        <div style={{ display: 'grid', gap: 10 }}>
          <div>
            <label style={lbl}>Title *</label>
            <input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={lbl}>Short Code (optional)</label>
            <input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} style={inputStyle} placeholder="e.g. POL-01" />
          </div>
          <div>
            <label style={lbl}>Summary</label>
            <textarea rows={2} value={form.summary} onChange={e => setForm({ ...form, summary: e.target.value })} style={{ ...inputStyle, fontFamily: 'inherit' }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" onClick={onClose} style={toolBtn()}>Cancel</button>
          <button type="submit" style={{ ...toolBtn(true), background: theme.primary, color: 'white', borderColor: theme.primary }}><CheckCircle2 size={13} /> Create</button>
        </div>
      </form>
    </div>
  );
};

const toolBtn = (primary) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '5px 10px', borderRadius: 6,
  border: `1px solid ${theme.border}`,
  background: primary ? theme.primary + '12' : 'white',
  color: primary ? theme.primaryDark : theme.navy,
  cursor: 'pointer', fontSize: 12, fontWeight: 600,
});
const modalBackdrop = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const modalStyle = { background: 'white', borderRadius: 12, padding: 22, width: '100%', maxWidth: 480, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' };
const lbl = { display: 'block', fontSize: 11.5, fontWeight: 700, color: theme.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 };

export default EditableDocsPage;
